import { parse } from 'yaml';

/**
 * The Camel components the data plane can actually load.
 *
 * A Quarkus BOM fixes versions; it does not put an extension on the classpath. So a flow naming
 * a component the runtime was not built with does not fail politely — it fails at activation,
 * after the publish looked successful, and every replica refuses the version while the previous
 * one keeps serving. Checking here turns that into a message the author can act on.
 *
 * This list is the plane's `pom.xml`, and `SupportedComponentsTest` there fails if the two
 * drift apart. Adding a component means adding its dependency first.
 */
export const RUNTIME_COMPONENTS = [
  'direct',
  'http',
  'https',
  'json-validator',
  'kafka',
  'platform-alert',
  'platform-contract',
  'platform-field',
  'platform-http',
  'platform-identity',
  'platform-reshape',
  'platform-wrap',
  'xslt-saxon',
] as const;

/**
 * The kinds of failure the platform can announce.
 *
 * This is `FailureAlert.Kind` in the data plane. A misspelled one does not fail there — losing an
 * alert would be worse than mistitling it — so it arrives under the wrong heading and nobody
 * notices for weeks. Here it can simply be corrected before publishing.
 */
export const ALERT_KINDS = ['undelivered', 'unprocessable', 'version-refused', 'ingress-failed'] as const;

/** Every `kafka` node in a flow, wherever it is nested. */
const queueSteps = (node: unknown, found: Record<string, unknown>[] = []): Record<string, unknown>[] => {
  if (Array.isArray(node)) {
    node.forEach((item) => queueSteps(item, found));

    return found;
  }
  if (!node || typeof node !== 'object') return found;

  for (const value of Object.values(node as Record<string, unknown>)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const held = value as Record<string, unknown>;
      if (schemeOf(held.uri) === 'kafka') found.push(held);
    }
    queueSteps(value, found);
  }

  return found;
};

/** The topic a queue node names, written either into the URI or beside it as a parameter. */
const topicOf = (step: Record<string, unknown>): string => {
  const uri = typeof step.uri === 'string' ? step.uri : '';
  const inUri = uri.includes(':') ? uri.slice(uri.indexOf(':') + 1).split('?')[0].trim() : '';
  if (inUri) return inUri;

  const parameters = step.parameters as Record<string, unknown> | undefined;

  return typeof parameters?.topic === 'string' ? parameters.topic.trim() : '';
};

/** Every endpoint a flow names, whole, in the order they appear. */
const urisOf = (node: unknown, found: string[] = []): string[] => {
  if (Array.isArray(node)) {
    node.forEach((item) => urisOf(item, found));

    return found;
  }
  if (!node || typeof node !== 'object') return found;

  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (key === 'uri' && typeof value === 'string' && value) found.push(value.trim());
    else urisOf(value, found);
  }

  return found;
};

/**
 * Why a drawn flow could not run, in the runtime's own words — empty when it can.
 *
 * These are exactly the conditions `AuthoredFlow.forRuntime` refuses in the data plane, checked
 * here so an author is told at publish rather than discovering it from a replica that quietly
 * refused the version. The plane is still the authority: it checks again on activation, runs the
 * canary through the flow and keeps the previous version serving if anything fails. This only
 * moves the message to where it can be acted on.
 */
type Drawn = { uri?: string; steps?: unknown[] };

const routesOf = (parsed: unknown): Drawn[] => {
  const entries = Array.isArray(parsed) ? parsed : [parsed];

  return entries
    .map((entry) => (entry as { route?: { from?: unknown } } | null)?.route?.from)
    .filter((from): from is Drawn => Boolean(from && typeof from === 'object'));
};

const schemeOf = (uri: unknown): string => (typeof uri === 'string' ? uri.split(':')[0].trim() : '');

const lastStepScheme = (steps: unknown[]): string => {
  const last = steps.at(-1) as { to?: unknown } | undefined;
  const to = last?.to;
  if (typeof to === 'string') return schemeOf(to);

  return schemeOf((to as { uri?: string } | undefined)?.uri);
};

/** The last Kafka topic a drawn flow's workers send to, leaving out the platform's own topics. */
export const drawnTopic = (source: string | undefined, ownTopics: readonly string[]): string | undefined => {
  if (!source?.trim()) return undefined;
  let parsed: unknown;
  try {
    parsed = parse(source);
  } catch {
    return undefined;
  }
  const workers = routesOf(parsed).filter((route) => schemeOf(route.uri) !== 'platform-http');

  return queueSteps(workers.map((route) => route.steps))
    .map(topicOf)
    .filter((topic) => topic && !ownTopics.includes(topic))
    .at(-1);
};

export const flowIssues = (source: string): string[] => {
  if (!source.trim()) return [];

  let parsed: unknown;
  try {
    parsed = parse(source);
  } catch (cause) {
    return [`This flow is not valid YAML: ${cause instanceof Error ? cause.message : 'it could not be read'}.`];
  }

  const routes = routesOf(parsed);
  if (routes.length === 0) return ['This flow contains no route, so there is nothing to run.'];

  const ingress = routes.find((route) => schemeOf(route.uri) === 'platform-http');
  const workers = routes.filter((route) => route !== ingress);
  const issues: string[] = [];

  if (!ingress) {
    issues.push('No route starts at the platform ingress, so vendor traffic would never enter this flow.');
  } else {
    const steps = Array.isArray(ingress.steps) ? ingress.steps : [];
    if (steps.length === 0) {
      issues.push('The ingress route has no steps, so it would accept a payload and do nothing with it.');
    } else if (lastStepScheme(steps) !== 'kafka') {
      issues.push(
        'The ingress route must end at a queue. The vendor is answered when its last step completes, so ending anywhere else answers before the message is durable.',
      );
    }
  }

  for (const worker of workers) {
    const scheme = schemeOf(worker.uri);
    if (scheme !== 'kafka') {
      issues.push(
        `A second route starts at \`${scheme || 'nothing'}\`. Everything after the ingress is entered from the queue, so only a kafka node can start it.`,
      );
      continue;
    }
    if (!Array.isArray(worker.steps) || worker.steps.length === 0)
      issues.push('The worker route has no steps, so a message would be read from the queue and dropped.');
  }

  const uris = [...new Set(routes.flatMap((route) => urisOf(route.steps ?? [])))];
  const named = [...new Set(uris.map((uri) => uri.split(':')[0].trim()))];
  for (const component of named.filter((candidate) => !RUNTIME_COMPONENTS.includes(candidate as never)))
    issues.push(
      `The runtime cannot run \`${component}\`. It can run ${RUNTIME_COMPONENTS.join(', ')} — publishing this would be refused by every replica, leaving the previous version serving.`,
    );

  /*
   * A queue node has to say which queue.
   *
   * The runtime used to fill in the platform's own topic wherever one was left blank, which meant
   * a flow could be drawn incomplete and still work — until it did not, and the reason was a piece
   * of behaviour nowhere on the canvas. An author now says which topic, and what they say is what
   * runs; the console offers the platform's own names rather than filling them in behind them.
   */
  for (const step of routes.flatMap((route) => queueSteps(route.steps ?? []))) {
    if (!topicOf(step))
      issues.push(
        'A queue node does not say which topic it writes to. Name the topic on that node — the platform will not choose one for you, and a node without one is refused by every replica.',
      );
  }

  for (const uri of uris.filter((candidate) => candidate.startsWith('platform-alert:'))) {
    const kind = uri.slice('platform-alert:'.length).split('?')[0].trim().toLowerCase();
    if (kind && !ALERT_KINDS.includes(kind as never))
      issues.push(
        `\`${uri}\` is not a kind of alert the platform knows. It knows ${ALERT_KINDS.join(', ')} — anything else still alerts, but under the wrong heading, which sends people to look in the wrong place.`,
      );
  }

  return issues;
};
